import type { CrudFilters, CrudSorting, DataProvider } from '@refinedev/core';
import restProvider, { axiosInstance } from '@refinedev/simple-rest';
import * as rison from 'rison';
import { logger } from 'shiver-core/logger';
import z from 'zod';

export function useRestProvider(apiUrl: URL, httpClient = axiosInstance): DataProvider {
  const original = restProvider(apiUrl.toString(), httpClient);

  return {
    ...original,
    getList: async ({ resource, pagination, filters, sorters, meta }) => {
      const url = `${apiUrl}/${resource}`;

      const { current = 1, pageSize = 10, mode = 'server' } = pagination ?? {};

      const { headers: headersFromMeta, method } = meta ?? {};
      const requestMethod = method ?? 'get';

      const query = new URLSearchParams();

      if (mode === 'server') {
        query.set('_start', String((current - 1) * pageSize));
        query.set('_end', String(current * pageSize));
      }

      const generatedSort = generateSort(sorters);
      if (generatedSort) {
        const { _sort, _order } = generatedSort;
        query.set('_sort', _sort.join(','));
        query.set('_order', _order.join(','));
      }

      if (filters && filters.length > 0) {
        const encoded = rison.encode_array(generateFilter(filters));
        query.set('_filter', encoded);
      }

      const urlWithQuery = query.size > 0 ? `${url}?${query}` : url;

      const { data, headers } = await httpClient[requestMethod as 'get'](urlWithQuery, {
        headers: headersFromMeta,
      });

      const total = +headers['x-total-count'];

      return {
        data,
        total: total || data.length,
      };
    },
  };
}

type Sort = { _sort: Array<string>; _order: Array<string> };

function generateSort(sorters?: CrudSorting): Sort | undefined {
  if (sorters && sorters.length > 0) {
    const _sort: string[] = [];
    const _order: string[] = [];

    for (const item of sorters) {
      _sort.push(item.field);
      _order.push(item.order);
    }

    return {
      _sort,
      _order,
    };
  }

  return;
}

const NullFilter = z
  .object({
    equals: z.null(),
    not: z.undefined(),
  })
  .partial();
const StringFilter = z
  .object({
    equals: z.string(),
    in: z.array(z.string()),
    contains: z.string(),
    startsWith: z.string(),
    endsWith: z.string(),
    gt: z.string(),
    gte: z.string(),
    lt: z.string(),
    lte: z.string(),
    not: z.undefined(),
  })
  .partial();
const NumberFilter = z
  .object({
    equals: z.number(),
    in: z.array(z.number()),
    lt: z.number(),
    lte: z.number(),
    gt: z.number(),
    gte: z.number(),
    not: z.undefined(),
  })
  .partial();
const BooleanFilter = z.object({
  equals: z.boolean(),
  not: z.undefined(),
});

const orNot = <T extends z.ZodObject>(type: T) => type.or(z.object({ not: type }));

export const Filter = z
  .object({
    field: z.string(),
  })
  .and(
    z.discriminatedUnion('type', [
      z.object({
        type: z.literal('null'),
        filter: orNot(NullFilter),
      }),
      z.object({
        type: z.literal('string'),
        filter: orNot(StringFilter),
      }),
      z.object({
        type: z.literal('number'),
        filter: orNot(NumberFilter),
      }),
      z.object({
        type: z.literal('boolean'),
        filter: BooleanFilter,
      }),
    ]),
  );
export type Filter = z.infer<typeof Filter>;

function generateFilter(filters: CrudFilters): Array<Filter> {
  const generated: Array<Filter> = [];

  for (const filter of filters) {
    switch (filter.operator) {
      case 'and':
      case 'or':
        throw new Error('conditional (AND/OR) filters are not supported yet');
    }

    const { field, value: rawValue } = filter;

    const op = filter.operator;

    switch (op) {
      case 'in':
      case 'nin':
        {
          if (!(rawValue instanceof Array) || rawValue.length === 0) continue;
          const normalizedItems = rawValue.map(normalizeFilterValue).filter((v) => v != null);
          if (normalizedItems.length === 0) continue;
          const type = normalizedItems[0]![0];
          const values = normalizedItems.map((v) => v[1]);

          generated.push(
            Filter.parse({
              field,
              type,
              filter: op === 'nin' ? { not: { in: values } } : { in: values },
            }),
          );
        }
        continue;
      case 'null':
      case 'nnull':
        generated.push(
          Filter.parse({
            field,
            type: 'null',
            filter: op === 'nnull' ? { not: { equals: null } } : { equals: null },
          }),
        );
        continue;
    }

    const normalized = normalizeFilterValue(rawValue);
    if (!normalized) {
      logger.warn(`Unsupported filter value type`, filter);
      continue;
    }

    const [type, value] = normalized;

    switch (op) {
      case 'eq':
      case 'ne':
        generated.push({
          field,
          type: type as 'string',
          filter: op.startsWith('n')
            ? { not: { equals: value as 'string' } }
            : { equals: value as 'string' },
        });
        continue;
      case 'lt':
      case 'lte':
      case 'gt':
      case 'gte':
        if (type === 'number' || type === 'string')
          generated.push({ field, type, filter: { [op]: value } });
        continue;
      case 'contains':
      case 'ncontains':
        if (type === 'string')
          generated.push({
            field,
            type,
            filter: op.startsWith('n') ? { not: { contains: value } } : { contains: value },
          });
        continue;
      case 'startswith':
        if (type == 'string') generated.push({ field, type, filter: { startsWith: value } });
        continue;
      case 'endswith':
        if (type === 'string') generated.push({ field, type, filter: { endsWith: value } });
        continue;
      default:
        logger.warn(`Unsupported filter operator`, filter);
    }
  }

  return generated.map((f) => Filter.parse(f));
}

function normalizeFilterValue(
  value: unknown,
): ['string', string] | ['number', number] | ['boolean', boolean] | ['null', null] | undefined {
  if (value instanceof Date) {
    return ['string', value.toISOString()];
  }

  if (value === null) {
    return ['null', null];
  }

  switch (typeof value) {
    case 'boolean':
      return ['boolean', value];
    case 'number':
      return ['number', value];
    case 'string':
      return ['string', value];
    default:
      return undefined;
  }
}
